import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from './messenger.service';
import { GenerateInvoiceRequest } from 'src/app/payer-billing/model/GenerateInvoiceRequest';
@Injectable({
  providedIn: 'root'
})
export class PayerBillingService implements OnDestroy
{
  public invoiceStatuses = [
    {
      key: 'INVOICE-STATUS-OPEN', value: 'OPEN', ordinalValue: 0, label: 'Open', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-ERROR', value: 'ERROR', ordinalValue: 1, label: 'Error', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-POSTED', value: 'POSTED', ordinalValue: 2, label: 'Posted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-EXTRACTED', value: 'EXTRACTED', ordinalValue: 3, label: 'Extracted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-SUBMITTED', value: 'SUBMITTED', ordinalValue: 4, label: 'Submitted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-ACCEPTED', value: 'ACCEPTED', ordinalValue: 5, label: 'Accepted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-REJECTED', value: 'REJECTED', ordinalValue: 6, label: 'Rejected', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-REPOSTED', value: 'REPOSTED', ordinalValue: 7, label: 'Reposted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-REEXTRACTED', value: 'REEXTRACTED', ordinalValue: 8, label: 'Reextracted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-RESUBMITTED', value: 'RESUBMITTED', ordinalValue: 9, label: 'Resubmitted', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-PROCESSED', value: 'PROCESSED', ordinalValue: 10, label: 'Processed', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'INVOICE-STATUS-CLOSED', value: 'CLOSED', ordinalValue: 11, label: 'Closed', icon: 'refresh',
      disabled: true, isValid: true
    },
  ];

  public invoiceItemStatuses = [
    {
      key: 'RECORD-STATUS-UNBILLED', value: 'UNBILLED', ordinalValue: 0, label: 'Unbilled', icon: 'check',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-MISSING-REQUIRED-FIELDS', value: 'MISSING_REQUIRED_FIELDS', ordinalValue: 1, label: 'Missing required fields', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-PAID', value: 'PAID', ordinalValue: 2, label: 'Paid', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-DENIED', value: 'DENIED', ordinalValue: 3, label: 'Denied', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-CANCELED', value: 'CANCELED', ordinalValue: 4, label: 'Canceled', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-VOID', value: 'VOID', ordinalValue: 5, label: 'Void', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-PENDING', value: 'PENDING', ordinalValue: 6, label: 'Pending', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-REBILL', value: 'REBILL', ordinalValue: 7, label: 'Rebill', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-NOT-ELIGIBLE', value: 'NOT_ELIGIBLE', ordinalValue: 8, label: 'Not eligible', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-INSURANCES-OVERLAP', value: 'INSURANCES_OVERLAP', ordinalValue: 9, label: 'Insurances overlap', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-INFORMATIONAL-ONLY', value: 'INFORMATIONAL_ONLY', ordinalValue: 10, label: 'Informational Only', icon: 'refresh',
      disabled: true, isValid: true
    },
    {
      key: 'RECORD-STATUS-PENDING-REBILL', value: 'PENDING_REBILL', ordinalValue: 11, label: 'Pending Rebill', icon: 'refresh',
      disabled: true, isValid: true
    },    
  ];

  private messagingLanguageChangedSubscription: Subscription;

  constructor(private http: HttpClient,
    private translate: TranslateService,
    private messengerService: MessengerService)
  {
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'languageChanged')
        {
          this.loadLocalizations();
        }
      }
    });
  }

  public generateInvoice(generateInvoiceRequest: GenerateInvoiceRequest[]): Observable<any>  
  {
    return this.http.post('/billing/invoice', generateInvoiceRequest);
  }

  public updateInvoicesStatus(status: string, data: any): Observable<any>  // updates Invoice Status for multiple invoices at once
  {
    return this.http.post(`/billing/invoices/status/${status}`, data);
  }

  public updateInvoiceItemsStatuses(id: number, data: any[]): Observable<any>  
  {
    return this.http.post(`/billing/invoice/${id}/items/status`, data);
  }

  public updateInvoiceItemIdentifiers(id: number): Observable<any>  
  {
    return this.http.post(`/billing/invoice/${id}/items/identifier/update`, null);
  }

  public updateInvoiceItem(id: number, data: any): Observable<any>
  {
    return this.http.post(`/billing/invoice/${id}/item`, data);
  }

  public deleteInvoice(id: number): Observable<any>
  {
    return this.http.delete(`/billing/invoice/${id}`);
  }

  public deleteInvoiceItem(id: number): Observable<any>
  {
    return this.http.delete<any>(`/billing/invoice/item/${id}`);
  }

  public getInvoiceById(id: number): Observable<any>
  {
    return this.http.get<any>(`/billing/invoice/${id}`);
  }

  public getInvoiceItemList(id: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/billing/invoice/${id}/items/list`);
  }

  /**
   * Delete a list of invoices by ids.
   *
   * @param invoicesToDelete Cases ids to close and reason for closing.
   */
  public deleteInvoices(invoicesToDelete: any): Observable<any>
  {
    return this.http.post(`/billing/invoices/delete`, invoicesToDelete);
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get the Invoice Status description to use
   *
   * @param invoiceStatus Invoice Status
   * @returns string Invoice Status description
   */
  public getInvoiceStatusText(invoiceStatus: string): string
  {
    let text = invoiceStatus;
    const aValue = this.invoiceStatuses.find(x => x.value === invoiceStatus);
    if (aValue !== undefined)
    {
      text = aValue.label;
    }
    return text;
  }

  /**
   * Get the Record Status description to use
   *
   * @param invoiceStatus Record Status
   * @returns string Record Status description
   */
  public getInvoiceItemStatusText(invoiceItemStatus: string): string
  {
    let text = invoiceItemStatus;
    const aValue = this.invoiceItemStatuses.find(x => x.value === invoiceItemStatus);
    if (aValue !== undefined)
    {
      text = aValue.label;
    }
    return text;
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    const stagesTranslationKeys = this.invoiceStatuses.map(a => a.key);
    this.translate.get(stagesTranslationKeys).subscribe({
      next: (res: string) =>
      {
        for (let i = 0; i < this.invoiceStatuses.length; i++)
        {
          const key = 'INVOICE-STATUS-' + this.invoiceStatuses[i].value.replace(/_/g, '-');
          this.invoiceStatuses[i].label = res[key];
        }
      }
    });

    const stagesTranslationKeysRecord = this.invoiceItemStatuses.map(a => a.key);
    this.translate.get(stagesTranslationKeysRecord).subscribe({
      next: (res: string) =>
      {
        for (let i = 0; i < this.invoiceItemStatuses.length; i++)
        {
          const key = 'RECORD-STATUS-' + this.invoiceItemStatuses[i].value.replace(/_/g, '-');
          this.invoiceItemStatuses[i].label = res[key];
        }
      }
    });
  }
}
